import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import HandleSetupModal from '../components/HandleSetupModal';
import TierProgressCard from '../components/TierProgressCard';
import EditSeriesModal from '../components/EditSeriesModal';
import EditArtworkModal from '../components/EditArtworkModal';
import CoverImage from '../components/CoverImage';
import OnboardingChecklist from '../components/OnboardingChecklist';
import OnboardingBanner from '../components/OnboardingBanner';
import {
  Upload,
  PlusCircle,
  User,
  Eye,
  Heart,
  MessageCircle,
  DollarSign,
  Edit,
  BarChart3,
  Trash2,
  Calendar,
  TrendingUp,
  TrendingDown,
  ExternalLink,
  BookOpen,
  FileText,
  Image,
  ArrowUpDown,
  MoreVertical,
  Pencil,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const CreatorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [seriesList, setSeriesList] = useState([]);
  const [novelsList, setNovelsList] = useState([]);
  const [artworksList, setArtworksList] = useState([]);
  const [artworksSort, setArtworksSort] = useState('recent'); // recent or title
  const [creatorProfile, setCreatorProfile] = useState(null);
  const [showHandleModal, setShowHandleModal] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deletingArtwork, setDeletingArtwork] = useState(null);
  const [editingArtwork, setEditingArtwork] = useState(null);
  
  // Ref to prevent duplicate fetches
  const hasFetched = useRef(false);

  useEffect(() => {
    // Use user?.id to prevent re-fetch on object reference change
    if (user?.id && !hasFetched.current) {
      hasFetched.current = true;
      fetchDashboardData();
      fetchCreatorProfile();
      fetchArtworks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fail-soft with timeout
      const [dashboardRes, seriesRes, novelsRes] = await Promise.all([
        axios.get(`${API}/creator/dashboard`, { headers, timeout: 10000 }).catch(() => ({ data: null })),
        axios.get(`${API}/creator/series`, { headers, timeout: 10000 }).catch(() => ({ data: [] })),
        axios.get(`${API}/novels?mine=true`, { headers, timeout: 10000 }).catch(() => ({ data: { novels: [] } })),
      ]);

      setDashboardStats(dashboardRes.data);
      setSeriesList(seriesRes.data || []);
      // Handle novels response - it may be { novels: [...] } or just [...]
      const novels = novelsRes.data?.novels || novelsRes.data || [];
      setNovelsList(Array.isArray(novels) ? novels : []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard');
      setLoading(false);
    }
  };

  const fetchCreatorProfile = async () => {
    try {
      const response = await axios.get(`${API}/creators/me/profile`);
      setCreatorProfile(response.data);
    } catch (error) {
      console.error('Error fetching creator profile:', error);
    }
  };

  const fetchArtworks = async (sort = artworksSort) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/art/mine`, {
        params: { sort, limit: 50 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setArtworksList(response.data.artworks || []);
    } catch (error) {
      console.error('Error fetching artworks:', error);
    }
  };

  const handleArtworksSortChange = (newSort) => {
    setArtworksSort(newSort);
    fetchArtworks(newSort);
  };

  const handleDeleteArtwork = async (artworkId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/art/post/${artworkId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Artwork deleted successfully');
      setArtworksList(prev => prev.filter(a => a.id !== artworkId));
      setDeletingArtwork(null);
    } catch (error) {
      console.error('Error deleting artwork:', error);
      toast.error('Failed to delete artwork');
    }
  };

  const handleToggleArtworkVisibility = async (artwork) => {
    try {
      const token = localStorage.getItem('token');
      const newVisibility = artwork.settings?.visibility === 'public' ? 'private' : 'public';
      await axios.put(`${API}/art/post/${artwork.id}`, 
        { visibility: newVisibility },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Artwork ${newVisibility === 'public' ? 'published' : 'hidden'}`);
      fetchArtworks();
    } catch (error) {
      console.error('Error updating artwork:', error);
      toast.error('Failed to update artwork');
    }
  };

  const handleArtworkUpdated = (updatedArtwork) => {
    // Update artwork in list with cache busting
    setArtworksList(prev => prev.map(art => 
      art.id === updatedArtwork.id 
        ? { ...updatedArtwork, _cache_key: Date.now() }
        : art
    ));
    setEditingArtwork(null);
  };

  // Helper to resolve image URLs
  const resolveImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return url.startsWith('/') ? `${BACKEND_URL}${url}` : `${BACKEND_URL}/${url}`;
  };

  const handleViewPublicProfile = () => {
    const handle = creatorProfile?.profile?.handle;
    
    // Check if handle is valid (not null, undefined, empty, or just whitespace)
    if (!handle || typeof handle !== 'string' || handle.trim().length === 0) {
      setShowHandleModal(true);
    } else {
      navigate(`/creator/${handle.trim()}`);
    }
  };

  const handleEditSeries = (series) => {
    setEditingSeries(series);
    setShowEditModal(true);
  };

  const handleSeriesUpdated = (updatedSeries) => {
    if (!updatedSeries) {
      // Series was deleted: remove from list
      setSeriesList(prev => prev.filter(s => s.id !== editingSeries?.id));
    } else {
      // Update the series in the list
      setSeriesList(prev => 
        prev.map(s => s.id === updatedSeries.id ? updatedSeries : s)
      );
    }
    setShowEditModal(false);
    setEditingSeries(null);
  };

  const handleHandleSuccess = (newHandle) => {
    if (creatorProfile) {
      setCreatorProfile(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          handle: newHandle
        }
      }));
    }
    
    // Store handle in session for quick access
    sessionStorage.setItem('user_handle', newHandle);
    
    // The HandleSetupModal will handle the redirect
    // No need to navigate here as it's handled automatically
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <div className="text-xl font-medium">Loading creator dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-b border-orange-500/30">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-3xl font-black">
                {user.email?.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <h1 className="text-3xl font-black">Creator Studio</h1>
                <p className="text-gray-400">{dashboardStats?.creator_profile?.display_name || user.email}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <Link to="/creator/analytics">
                <Button 
                  variant="outline" 
                  className="border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Analytics
                </Button>
              </Link>
              <Link to="/create-series">
                <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold">
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Create New Series
                </Button>
              </Link>
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  onClick={handleViewPublicProfile}
                  className="border-gray-600 text-gray-300 hover:bg-gray-800"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Public Profile
                </Button>
                
                <Link to="/profile/editor/creator">
                  <Button 
                    variant="outline" 
                    className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Customize Profile
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Verification Banner - Show if not verified */}
          {creatorProfile?.profile && !creatorProfile.profile.human_verified && creatorProfile.profile.verification_status !== 'pending' && (
            <div className="mt-8 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-2 border-blue-500/30 rounded-xl p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-blue-500 rounded-full p-2">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Get Human Verified</h3>
                      <p className="text-blue-200 text-sm">Stand out from AI-generated content</p>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm mb-0">
                    Prove you're a real artist and earn the Human Verified badge. Show your fans authentic, human-made art!
                  </p>
                </div>
                <Link to="/verification">
                  <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold whitespace-nowrap">
                    Get Verified →
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Pending Verification Status */}
          {creatorProfile?.profile && creatorProfile.profile.verification_status === 'pending' && (
            <div className="mt-8 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-2 border-yellow-500/30 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <div className="bg-yellow-500 rounded-full p-2">
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Verification Pending</h3>
                  <p className="text-gray-400 text-sm">
                    Your verification request is under review. We'll notify you once it's been reviewed!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Onboarding Banner - Next Best Action */}
          <OnboardingBanner className="mt-8" />

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8">
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <BarChart3 className="w-8 h-8 text-orange-400" />
                  <TrendingUp className="w-5 h-5 text-green-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-1">
                  {dashboardStats?.total_views?.toLocaleString() || 0}
                </div>
                <div className="text-gray-400 text-sm">Total Views</div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <Heart className="w-8 h-8 text-red-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-1">
                  {dashboardStats?.total_subscribers?.toLocaleString() || 0}
                </div>
                <div className="text-gray-400 text-sm">Subscribers</div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <Upload className="w-8 h-8 text-blue-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-1">
                  {dashboardStats?.total_episodes || 0}
                </div>
                <div className="text-gray-400 text-sm">Episodes Published</div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign className="w-8 h-8 text-yellow-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-1">$0</div>
                <div className="text-gray-400 text-sm">Total Earnings</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Creator Tier Progress & Onboarding */}
      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tier Progress takes 2 columns */}
          <div className="lg:col-span-2">
            <TierProgressCard />
          </div>
          
          {/* Onboarding Checklist Widget */}
          <div className="lg:col-span-1">
            <OnboardingChecklist />
          </div>
        </div>
      </div>

      {/* Series Management */}
      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">My Series ({seriesList.length})</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {seriesList.map((series) => (
            <Card key={series.id} className="bg-gray-900 border-gray-800 hover:border-orange-500 transition-all group">
              <CardContent className="p-0">
                <div className="relative h-48 overflow-hidden">
                  <CoverImage 
                    item={series}
                    type="comic"
                    alt={series.title}
                    aspectRatio="16/9"
                    className="h-full rounded-none group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-3 right-3">
                    <Badge className={`${series.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'} text-white`}>
                      {series.status === 'completed' ? 'Completed' : 'Ongoing'}
                    </Badge>
                  </div>
                </div>

                <div className="p-4">
                  <h3 className="text-white font-bold text-lg mb-2 line-clamp-1">{series.title}</h3>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
                    <div className="text-center">
                      <div className="text-orange-400 font-bold">{series.metrics_cache?.views || 0}</div>
                      <div className="text-gray-400 text-xs">Views</div>
                    </div>
                    <div className="text-center">
                      <div className="text-orange-400 font-bold">{series.metrics_cache?.subscribers || 0}</div>
                      <div className="text-gray-400 text-xs">Subs</div>
                    </div>
                    <div className="text-center">
                      <div className="text-orange-400 font-bold">{series.metrics_cache?.chapters || 0}</div>
                      <div className="text-gray-400 text-xs">Episodes</div>
                    </div>
                  </div>

                  {series.latest_episode && (
                    <p className="text-gray-400 text-xs mb-4">
                      Latest: Ep {series.latest_episode.chapter_number} • {new Date(series.latest_episode.created_at).toLocaleDateString()}
                    </p>
                  )}

                  {/* Action Buttons */}
                  <div className="grid grid-cols-3 gap-2">
                    <Link to={`/series/${series.id}/upload`}>
                      <Button className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold text-xs">
                        <Upload className="w-3 h-3 mr-1" />
                        Upload
                      </Button>
                    </Link>
                    <Button 
                      onClick={() => handleEditSeries(series)}
                      variant="outline" 
                      className="w-full border-gray-600 text-gray-300 hover:bg-gray-800 text-xs"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Link to={`/series/${series.id}`}>
                      <Button variant="outline" className="w-full border-gray-600 text-gray-300 hover:bg-gray-800 text-xs">
                        <Eye className="w-3 h-3 mr-1" />
                        View
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {seriesList.length === 0 && (
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-12 text-center">
              <PlusCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No series yet</h3>
              <p className="text-gray-400 mb-6">Start your creative journey by creating your first series!</p>
              <Link to="/create-series">
                <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600">
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Create Your First Series
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Novels Management */}
      <div className="container mx-auto px-6 py-8 border-t border-gray-800">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold flex items-center">
            <BookOpen className="w-6 h-6 mr-2 text-green-400" />
            My Novels ({novelsList.length})
          </h2>
          <Link to="/create-novel">
            <Button className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold">
              <PlusCircle className="w-4 h-4 mr-2" />
              Create New Novel
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {novelsList.map((novel) => (
            <Card key={novel.id} className="bg-gray-900 border-gray-800 hover:border-green-500 transition-all group">
              <CardContent className="p-0">
                <div className="relative h-48 overflow-hidden">
                  <CoverImage 
                    item={novel}
                    type="novel"
                    alt={novel.title}
                    aspectRatio="16/9"
                    className="h-full rounded-none group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-3 right-3">
                    <Badge className={`${novel.status === 'completed' ? 'bg-green-500' : 'bg-emerald-500'} text-white`}>
                      {novel.status === 'completed' ? 'Completed' : 'Ongoing'}
                    </Badge>
                  </div>
                </div>

                <div className="p-4">
                  <h3 className="text-white font-bold text-lg mb-2 line-clamp-1">{novel.title}</h3>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
                    <div className="text-center">
                      <div className="text-green-400 font-bold">{novel.view_count || 0}</div>
                      <div className="text-gray-400 text-xs">Views</div>
                    </div>
                    <div className="text-center">
                      <div className="text-green-400 font-bold">{novel.chapter_count || 0}</div>
                      <div className="text-gray-400 text-xs">Chapters</div>
                    </div>
                    <div className="text-center">
                      <div className="text-green-400 font-bold">{novel.genres?.length || 0}</div>
                      <div className="text-gray-400 text-xs">Genres</div>
                    </div>
                  </div>

                  {novel.description && (
                    <p className="text-gray-400 text-xs mb-4 line-clamp-2">
                      {novel.description}
                    </p>
                  )}

                  {/* Action Buttons */}
                  <div className="grid grid-cols-3 gap-2">
                    <Link to={`/novel/${novel.id}/add-chapter`}>
                      <Button className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold text-xs">
                        <FileText className="w-3 h-3 mr-1" />
                        Add Ch.
                      </Button>
                    </Link>
                    <Link to={`/novel/${novel.id}`}>
                      <Button 
                        variant="outline" 
                        className="w-full border-gray-600 text-gray-300 hover:bg-gray-800 text-xs"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Manage
                      </Button>
                    </Link>
                    <Link to={`/novel/${novel.id}`}>
                      <Button variant="outline" className="w-full border-gray-600 text-gray-300 hover:bg-gray-800 text-xs">
                        <Eye className="w-3 h-3 mr-1" />
                        View
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {novelsList.length === 0 && (
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-12 text-center">
              <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No novels yet</h3>
              <p className="text-gray-400 mb-6">Start writing your first novel and share your stories!</p>
              <Link to="/create-novel">
                <Button className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600">
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Create Your First Novel
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Artworks Management */}
      <div className="container mx-auto px-6 py-8 border-t border-gray-800">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold flex items-center">
            <Image className="w-6 h-6 mr-2 text-pink-400" />
            My Artworks ({artworksList.length})
          </h2>
          <div className="flex items-center gap-3">
            {/* Sort Toggle */}
            <div className="flex items-center gap-2 bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => handleArtworksSortChange('recent')}
                className={`px-3 py-1 rounded text-sm transition-colors ${
                  artworksSort === 'recent' 
                    ? 'bg-pink-500 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Newest
              </button>
              <button
                onClick={() => handleArtworksSortChange('title')}
                className={`px-3 py-1 rounded text-sm transition-colors ${
                  artworksSort === 'title' 
                    ? 'bg-pink-500 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                A–Z
              </button>
            </div>
            <Link to="/post-art">
              <Button className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold">
                <PlusCircle className="w-4 h-4 mr-2" />
                Post Artwork
              </Button>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {artworksList.map((artwork) => (
            <Card 
              key={artwork.id} 
              className="bg-gray-900 border-gray-800 hover:border-pink-500 transition-all group overflow-hidden"
            >
              <CardContent className="p-0">
                {/* Image */}
                <Link to={`/art/${artwork.id}`} className="block relative aspect-square overflow-hidden">
                  {artwork.image_url ? (
                    <img 
                      src={resolveImageUrl(artwork.image_url)} 
                      alt={artwork.title || 'Artwork'} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                      <Image className="w-12 h-12 text-gray-600" />
                    </div>
                  )}
                  
                  {/* Status Badge */}
                  <div className="absolute top-2 right-2">
                    <Badge className={`${
                      artwork.settings?.visibility === 'public' 
                        ? 'bg-green-500' 
                        : 'bg-yellow-500'
                    } text-white text-xs`}>
                      {artwork.settings?.visibility === 'public' ? 'Published' : 'Hidden'}
                    </Badge>
                  </div>
                </Link>

                {/* Info */}
                <div className="p-3">
                  <h3 className="text-white font-medium text-sm line-clamp-1 mb-1">
                    {artwork.title || 'Untitled'}
                  </h3>
                  <p className="text-gray-500 text-xs">
                    {artwork.created_at ? new Date(artwork.created_at).toLocaleDateString() : ''}
                  </p>

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setEditingArtwork(artwork)}
                      className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800 text-xs"
                      data-testid={`edit-artwork-${artwork.id}`}
                    >
                      <Pencil className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Link to={`/art/${artwork.id}`}>
                      <Button variant="outline" size="sm" className="border-gray-700 text-gray-300 hover:bg-gray-800 text-xs">
                        <Eye className="w-3 h-3" />
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleToggleArtworkVisibility(artwork)}
                      className="border-gray-700 text-gray-300 hover:bg-gray-800"
                      title={artwork.settings?.visibility === 'public' ? 'Hide' : 'Publish'}
                    >
                      {artwork.settings?.visibility === 'public' ? (
                        <Eye className="w-3 h-3" />
                      ) : (
                        <ExternalLink className="w-3 h-3" />
                      )}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setDeletingArtwork(artwork)}
                      className="border-red-800 text-red-400 hover:bg-red-900/50"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {artworksList.length === 0 && (
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-12 text-center">
              <Image className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No artworks yet</h3>
              <p className="text-gray-400 mb-6">Share your artwork with the community!</p>
              <Link to="/post-art">
                <Button className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600">
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Post Your First Artwork
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Artwork Confirmation Modal */}
      {deletingArtwork && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="bg-gray-900 border-gray-700 max-w-md w-full">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">Delete Artwork?</h3>
              <p className="text-gray-400 mb-4">
                Are you sure you want to delete "{deletingArtwork.title || 'Untitled'}"? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1 border-gray-600"
                  onClick={() => setDeletingArtwork(null)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={() => handleDeleteArtwork(deletingArtwork.id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Handle Setup Modal */}
      <HandleSetupModal
        open={showHandleModal}
        onClose={() => setShowHandleModal(false)}
        onSuccess={handleHandleSuccess}
      />

      {/* Edit Series Modal */}
      {editingSeries && (
        <EditSeriesModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingSeries(null);
          }}
          series={editingSeries}
          onUpdate={handleSeriesUpdated}
        />
      )}

      {/* Edit Artwork Modal */}
      {editingArtwork && (
        <EditArtworkModal
          artwork={editingArtwork}
          onClose={() => setEditingArtwork(null)}
          onUpdate={handleArtworkUpdated}
        />
      )}
    </div>
  );
};

export default CreatorDashboard;
